import connectwiseConfig from "@/lib/adapters/connectwise.json";
import { BasePSAAdapter, mapRowWithMapping } from "@/lib/adapters/base";
import type { ColumnMapping, RawRow } from "@/lib/adapters/base";
import type { NormalizedRow } from "@/types";

/** Shape of a bundled PSA adapter JSON configuration. */
interface AdapterConfigJson {
  name: string;
  version: string;
  detection: { required_headers: string[]; optional_headers?: string[] };
  column_mapping: Record<string, string>;
  status_values: { active: string[]; inactive?: string[] };
}

const config = connectwiseConfig as AdapterConfigJson;

/**
 * Adapter for ConnectWise Manage product-catalog exports. Detection signatures,
 * column mapping, and status values come from the bundled `connectwise.json`
 * (a copy of `shared/adapters/connectwise.json`).
 */
export class ConnectWiseAdapter extends BasePSAAdapter {
  readonly name: string;
  readonly version: string;
  detectionSignatures: string[];
  private readonly requiredHeaders: string[];
  private readonly columnMapping: ColumnMapping;

  constructor() {
    super();
    this.name = config.name;
    this.version = config.version;
    this.requiredHeaders = config.detection.required_headers;
    this.detectionSignatures = [...this.requiredHeaders];
    this.columnMapping = config.column_mapping;
    this.activeValues = new Set(config.status_values.active.map((value) => value.toLowerCase()));
  }

  /** Fraction of required headers present (case-insensitive, whitespace-trimmed). */
  detect(headers: string[]): number {
    if (this.requiredHeaders.length === 0) return 0;
    const present = new Set(headers.map((header) => header.trim().toLowerCase()));
    const matched = this.requiredHeaders.filter((header) =>
      present.has(header.toLowerCase()),
    ).length;
    return matched / this.requiredHeaders.length;
  }

  mapRow(row: RawRow): NormalizedRow {
    return mapRowWithMapping(row, this.columnMapping);
  }

  statusIsActive(row: RawRow): boolean {
    const column = this.columnMapping["status"];
    if (column === undefined) return true;
    const value = row[column];
    if (value === undefined) return false;
    return this.activeValues.has(value.trim().toLowerCase());
  }
}
